import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import LoginWidget from '../widgets/LoginWidget';
import { useAuth } from '../providers/AuthContext';
import { getAppLogoSrc, getSiteTitle, isPdsaMode } from '../utils/appBranding';
import { useDataManagerAccess } from '../hooks/useDataManagerAccess';
import {
    DATA_MANAGER_HOME,
    DATA_MANAGER_WORKSPACE_LABEL,
} from '../constants/setupRoadmapSteps';
import api from '../API';
import styles from './PdsaHome.module.css';

/** Girder AccessType.WRITE — delegated org/folder managers, not project-participant WRITE. */
const GIRDER_ACCESS_WRITE = 1;

function folderHasHierarchyWrite(folder) {
    const type = folder?.girderProjects?.type;
    if (type !== 'organization' && type !== 'project-subfolder') return false;
    return typeof folder.myAccess === 'number' && folder.myAccess >= GIRDER_ACCESS_WRITE;
}

function canManageProjectHierarchy(rootResponse) {
    if (rootResponse?.status !== 200 || !rootResponse.data) return false;
    const data = rootResponse.data;
    if (data.canCreateAtRoot) return true;
    const folders = [...(data.folders || []), ...(data.accessPoints || [])];
    return folders.some(folderHasHierarchyWrite);
}

function NavCard({ to, title, description, primary }) {
    const className = `${styles.card} ${styles.cardInteractive}${primary ? ` ${styles.cardPrimary}` : ''}`;
    return (
        <Link to={to} className={className}>
            <span className={styles.cardAccent} aria-hidden />
            <span className={styles.cardBody}>
                <span className={styles.cardTitle}>{title}</span>
                <span className={styles.cardDescription}>{description}</span>
            </span>
            <span className={styles.cardChevron} aria-hidden>
                ›
            </span>
        </Link>
    );
}

function AdminToolsCard() {
    return (
        <div className={`${styles.card} ${styles.cardMenu}`}>
            <span className={styles.cardAccent} aria-hidden />
            <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Administrator Tools</span>
                <span className={styles.cardDescription}>
                    Site setup, users, and the Girder backend.
                </span>
                <div className={styles.cardLinks}>
                    <Link to="/admin" className={styles.cardLink}>
                        Site administration
                        <span aria-hidden>›</span>
                    </Link>
                    <Link to={DATA_MANAGER_HOME} className={styles.cardLink}>
                        {DATA_MANAGER_WORKSPACE_LABEL}
                        <span aria-hidden>›</span>
                    </Link>
                    <a href="/dsa" className={styles.cardLink}>
                        Digital Slide Archive
                        <span aria-hidden>›</span>
                    </a>
                </div>
            </div>
        </div>
    );
}

function DataManagerToolsCard() {
    const description = isPdsaMode()
        ? 'Definitions, cases, and PathPresenter import.'
        : 'Open the Data Manager workspace.';

    return (
        <div className={`${styles.card} ${styles.cardMenu}`}>
            <span className={styles.cardAccent} aria-hidden />
            <div className={styles.cardBody}>
                <span className={styles.cardTitle}>Data management</span>
                <span className={styles.cardDescription}>{description}</span>
                <div className={styles.cardLinks}>
                    <Link to={DATA_MANAGER_HOME} className={styles.cardLink}>
                        {DATA_MANAGER_WORKSPACE_LABEL}
                        <span aria-hidden>›</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}

function PdsaHome() {
    const { user, pending } = useAuth();
    const { isDataManager, pending: dataManagerPending } = useDataManagerAccess();
    const siteTitle = getSiteTitle();
    const logo = getAppLogoSrc();
    const isSiteAdmin = !!(user && user.admin);

    const { data: rootResponse, isLoading: rootLoading } = useQuery({
        queryKey: ['projects-root'],
        queryFn: () => api.get('projects/root', {}),
        enabled: !!user && !pending && !isSiteAdmin,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    const showAdminTools = isSiteAdmin;
    const showBrowse = isSiteAdmin || canManageProjectHierarchy(rootResponse);
    const showDataManagerCard = isDataManager && !isSiteAdmin;
    const waitingForRoles =
        pending
        || (!!user && !isSiteAdmin && rootLoading)
        || (!!user && dataManagerPending);

    const cardCount =
        1 + (showBrowse ? 1 : 0) + (showDataManagerCard ? 1 : 0) + (showAdminTools ? 1 : 0);
    const cardsClass = [
        styles.cards,
        cardCount === 2 ? styles.cardsTwo : '',
        cardCount >= 3 ? styles.cardsThree : '',
    ].filter(Boolean).join(' ');

    let subtitle = 'Sign in to open your projects.';
    if (user) {
        if (isSiteAdmin) {
            subtitle = 'Choose a workspace or open site administration.';
        } else if (showBrowse || showDataManagerCard) {
            subtitle = 'Open your assignments, or browse and organize projects.';
        } else {
            subtitle = 'Open annotation projects assigned to you.';
        }
    }

    return (
        <div className={styles.page}>
            <header className={styles.hero}>
                <img src={logo} className={styles.logo} alt="" />
                <h1 className={styles.title}>{siteTitle}</h1>
                <p className={styles.subtitle}>{subtitle}</p>
            </header>
            {waitingForRoles ? (
                <p className={styles.status}>Loading…</p>
            ) : !user ? (
                <div className={styles.loginWrap}>
                    <LoginWidget alwaysOpen title="Log in to continue" />
                </div>
            ) : (
                <nav className={cardsClass} aria-label="Site destinations">
                    <NavCard
                        to="/projects"
                        primary
                        title="My projects"
                        description="Open annotation projects assigned to you."
                    />
                    {showBrowse ? (
                        <NavCard
                            to="/projects/manage"
                            title="Browse and organize"
                            description="Create and manage the project hierarchy."
                        />
                    ) : null}
                    {showDataManagerCard ? <DataManagerToolsCard /> : null}
                    {showAdminTools ? <AdminToolsCard /> : null}
                </nav>
            )}
        </div>
    );
}

export default PdsaHome;
